import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Improved logout function
  const logout = () => {
    console.log('🚪 Logging out - clearing all auth data');
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    setLoading(false); // Ensure loading is false after logout
  };

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      console.log('🔍 Found saved token, validating...');
      setToken(savedToken);
      fetchUserData(savedToken);
    } else {
      console.log('📝 No saved token found');
      setLoading(false);
    }
  }, []);

  const fetchUserData = async (authToken) => {
    try {
      console.log('🔄 Fetching user data with token...');
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        console.log('✅ User data fetched successfully:', userData.email);
        setUser(userData);
        setLoading(false);
      } else {
        const errorData = await response.json();
        console.log('❌ Token validation failed:', errorData.error);
        
        // Token invalid or expired, clear everything immediately
        logout();
        
        // If token was expired, show a user-friendly message
        if (errorData.expired) {
          console.log('⏰ Token expired - user needs to log in again');
          // Could show a toast notification here
        }
      }
    } catch (error) {
      console.error('🚨 Error fetching user data:', error);
      logout();
    }
  };

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login for:', email);
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Login successful');
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('token', data.token);
        return { success: true };
      } else {
        const errorData = await response.json();
        console.log('❌ Login failed:', errorData.error);
        return { success: false, error: errorData.error };
      }
    } catch (error) {
      console.error('🚨 Login network error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // Improved isAuthenticated computation with better validation
  const isAuthenticated = !!(token && user && !loading);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated
  };

  // Debug logging for auth state changes
  useEffect(() => {
    console.log('🔄 Auth state changed:', { 
      hasToken: !!token, 
      hasUser: !!user, 
      loading, 
      isAuthenticated 
    });
  }, [token, user, loading, isAuthenticated]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
